/**
 * components/BatchResultsView.jsx
 * Scrollable list of result sections — one per file in the batch queue.
 * Completed files show ResultsTable + download/annotated tabs.
 * Failed files show an error notice.
 * Multi-file batches also offer a "Combined View" tab that merges all
 * inspection items so the total matches the dashboard batch-row count.
 */

import React, { useRef, useState, useMemo, useEffect } from 'react';
import ResultsTable from './ResultsTable';
import TabularResultsView from './TabularResultsView';
import { fmtTolPlus, fmtTolMinus } from '../utils/tolerance';
import AnnotatedImage from './AnnotatedImage';
import API from '../services/api';
import '../styles/components.css';

function formatScanMs(ms) {
  if (ms == null || ms === '') return null;
  const n = Number(ms);
  if (!Number.isFinite(n) || n < 0) return null;
  if (n >= 60000) {
    return `${Math.floor(n / 60000)}m ${Math.round((n % 60000) / 1000)}s`;
  }
  return `${(n / 1000).toFixed(1)}s`;
}

/* ── Single file result section ─────────────────────────────── */
function FileSectionHeader({ item, totalFiles, index }) {
  const completedCount = totalFiles; // already filtered to completed before render
  return (
    <div className="brs-section-header">
      <div className="brs-section-title-row">
        <div className="brs-file-badge" style={{
          display: 'inline-flex', alignItems: 'center', gap: 7,
          background: 'rgba(249,115,22,0.10)', border: '1px solid rgba(249,115,22,0.35)',
          color: '#EA6A0E', borderRadius: 9, padding: '7px 11px',
          fontWeight: 700, fontSize: 12, flex: 'none',
        }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14 2 14 8 20 8"/>
            <line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
          </svg>
          {index + 1} / {completedCount}
        </div>
        <div className="brs-section-info">
          <h3 className="brs-section-filename">{item.fileName}</h3>
          {item.results && (
            <div className="brs-section-stats">
              <span>{item.results.cc_count ?? 0} CC symbols</span>
              <span className="brs-stat-sep">·</span>
              <span>{item.results.item_count ?? 0} inspection items</span>
              {item.results.metadata?.part_no && (
                <>
                  <span className="brs-stat-sep">·</span>
                  <span>Part {item.results.metadata.part_no}</span>
                </>
              )}
              {formatScanMs(item.results?.scan_duration_ms) && (
                <>
                  <span className="brs-stat-sep">·</span>
                  <span>Scan {formatScanMs(item.results.scan_duration_ms)}</span>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** Rare race fallback: gated navigation should always prefetch first */
function FileResultSectionPending({ item, totalFiles, sectionIndex }) {
  return (
    <div className="brs-section">
      <FileSectionHeader item={item} totalFiles={totalFiles} index={sectionIndex} />
      <div className="brs-section-pending-body" role="status" aria-live="polite">
        Loading inspection data…
      </div>
    </div>
  );
}

function FileResultSection({ item, queueIdx, totalFiles, sectionIndex, onResultUpdated }) {
  const [activeTab,      setActiveTab]      = useState('results');
  const [dlOpen,         setDlOpen]         = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [dlError,        setDlError]        = useState(null);
  const dlMenuRef = useRef(null);

  const { fileId, fileName, results } = item;

  const sanitize = (name) => (name || '').toString().trim().replace(/[\\/:*?"<>|]/g, '_');
  const base     = results.metadata?.part_no ? sanitize(results.metadata.part_no) : sanitize(fileName);

  const downloadExcel = async () => {
    setDlOpen(false);
    try { await API.downloadExcel(fileId, `${base} - Inspection Plan.xlsx`); }
    catch (err) { setDlError(err.error || 'Download failed'); }
  };

  const downloadPdf = async () => {
    setDlOpen(false);
    setDownloadingPdf(true);
    try { await API.downloadPdf(fileId, `${base} - Inspection Plan.pdf`); }
    catch (err) { setDlError(err.error || 'PDF download failed'); }
    finally { setDownloadingPdf(false); }
  };

  const downloadAnnotated = async () => {
    try { await API.downloadAnnotatedImage(fileId); }
    catch (err) { setDlError(err.error || 'Image download failed'); }
  };

  return (
    <div className="brs-section">
      <FileSectionHeader item={item} totalFiles={totalFiles} index={sectionIndex} />

      {dlError && (
        <div className="brs-dl-error">
          <span>{dlError}</span>
          <button onClick={() => setDlError(null)}>×</button>
        </div>
      )}

      {/* Download button */}
      <div className="brs-actions-row">
        <div style={{ position: 'relative' }} ref={dlMenuRef}>
          <button
            className="download-button primary"
            onClick={() => setDlOpen(o => !o)}
            disabled={downloadingPdf}
          >
            {downloadingPdf ? 'Downloading…' : '↓ Download Report ▾'}
          </button>
          {dlOpen && (
            <div className="brs-dl-menu">
              <button
                className="brs-dl-menu-item"
                onClick={downloadExcel}
              >
                Excel (.xlsx)
              </button>
              <button
                className="brs-dl-menu-item"
                onClick={downloadPdf}
              >
                PDF (.pdf)
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs-row">
        <button className={`tab-button ${activeTab === 'results' ? 'active' : ''}`} onClick={() => setActiveTab('results')}>
          Results Table
        </button>
        <button className={`tab-button ${activeTab === 'drawing' ? 'active' : ''}`} onClick={() => setActiveTab('drawing')}>
          Original Drawing
        </button>
      </div>

      {activeTab === 'results' && (
        (results.scan_type === 'tabular' || (Array.isArray(results.variants) && results.variants.length > 0)) ? (
          <TabularResultsView fileId={fileId} variants={results.variants || []} />
        ) : (
        <ResultsTable
          metadata={results.metadata}
          items={results.inspection_items}
          notesItems={results.notes_items || []}
          visualInspectionItems={results.visual_inspection_items}
          safetyHazardItems={results.safety_hazard_items || []}
          gdtTableRows={results.gdt_table_rows || []}
          gdtSymbolsUnused={results.gdt_symbols_unused || []}
          lowConfidenceDimensions={results.low_confidence_dimensions || []}
          onPromoteLowConfidence={async (lcIndex, destination, overrides) => {
            await API.promoteResultsRow(fileId, lcIndex, destination, overrides);
            try {
              const updated = await API.getResults(fileId);
              onResultUpdated(fileId, updated);
            } catch {}
          }}
          onDemoteDimension={async (specification, location) => {
            await API.demoteResultsRow(fileId, specification, location);
            try {
              const updated = await API.getResults(fileId);
              onResultUpdated(fileId, updated);
            } catch {}
          }}
          onDemoteNote={async (noteLabel, category) => {
            await API.demoteResultsNote(fileId, noteLabel, category);
            try {
              const updated = await API.getResults(fileId);
              onResultUpdated(fileId, updated);
            } catch {}
          }}
          onDemoteGdt={async (gdtItemNo) => {
            await API.demoteResultsGdt(fileId, gdtItemNo);
            try {
              const updated = await API.getResults(fileId);
              onResultUpdated(fileId, updated);
            } catch {}
          }}
          ccCount={results.cc_count}
          itemCount={results.item_count}
          fileId={fileId}
          onSaved={async () => {
            try {
              const updated = await API.getResults(fileId);
              onResultUpdated(fileId, updated);
            } catch {}
          }}
          blockTolerances={results.block_tolerances ?? {}}
          scanDurationMs={results.scan_duration_ms}
        />
        )
      )}

      {activeTab === 'drawing' && (
        <AnnotatedImage
          fileId={fileId}
          imageUrls={results.annotated_image_urls || (results.annotated_image_path ? [results.annotated_image_path] : [])}
          imageUrl={results.annotated_image_path}
          onDownload={downloadAnnotated}
        />
      )}
    </div>
  );
}

/* ── Combined view: all files' items merged into one table ────── */
function CombinedResultsView({ completedWithResults }) {
  const [search,     setSearch]     = useState('');
  const [typeFilter, setTypeFilter] = useState('all');

  const allItems = useMemo(() => {
    const merged = [];
    let n = 1;
    for (const item of completedWithResults) {
      const rows = item.results?.inspection_items || [];
      const label = item.results?.metadata?.part_no
        ? `Part ${item.results.metadata.part_no}`
        : item.fileName || `File ${item.fileId}`;
      for (const row of rows) {
        merged.push({ ...row, _global_no: n++, _source: label });
      }
    }
    return merged;
  }, [completedWithResults]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allItems.filter(item => {
      const typeOk = typeFilter === 'all' || (item.inspection_type || '').toLowerCase() === typeFilter;
      if (!typeOk) return false;
      if (!q) return true;
      const hay = [
        item.specification, item.location_on_drawing, item._source,
        item.inspection_type, item.inspection_tool,
      ].map(v => String(v ?? '').toLowerCase()).join(' ');
      return hay.includes(q);
    });
  }, [allItems, search, typeFilter]);

  const totalCC = allItems.filter(i => i.critical_characteristic === 'Yes').length;

  const fmtLimit = v => {
    if (v == null || v === '' || v === 'NA') return '—';
    const n = parseFloat(v);
    return isNaN(n) ? v : n.toFixed(3);
  };

  return (
    <div style={{ padding: '0 0 24px' }}>
      {/* Metric chips */}
      <div className="metrics-row" style={{ marginBottom: 16 }}>
        <div className="metric-box">
          <div className="metric-label">Total Items</div>
          <div className="metric-value">{allItems.length}</div>
        </div>
        <div className="metric-box">
          <div className="metric-label">CC Symbols</div>
          <div className="metric-value">{totalCC}</div>
        </div>
        <div className="metric-box">
          <div className="metric-label">Files</div>
          <div className="metric-value">{completedWithResults.length}</div>
        </div>
      </div>

      {/* Search / filter */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        <input
          className="table-search"
          placeholder="Search all items…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ flex: '1 1 200px', minWidth: 160 }}
        />
        <select
          className="table-filter"
          value={typeFilter}
          onChange={e => setTypeFilter(e.target.value)}
        >
          <option value="all">All types</option>
          <option value="dimensional">Dimensional</option>
          <option value="visual">Visual</option>
          <option value="functional">Functional</option>
        </select>
      </div>

      {/* Table */}
      <div className="table-container">
        <table className="results-table">
          <thead>
            <tr>
              <th style={{ minWidth: 40 }}>#</th>
              <th style={{ minWidth: 120 }}>Source</th>
              <th style={{ minWidth: 90 }}>Location</th>
              <th style={{ minWidth: 44 }}>CC</th>
              <th style={{ minWidth: 200 }}>Specification</th>
              <th style={{ minWidth: 70 }}>Nom.</th>
              <th style={{ minWidth: 60 }}>Tol+</th>
              <th style={{ minWidth: 60 }}>Tol−</th>
              <th style={{ minWidth: 70 }}>UCL</th>
              <th style={{ minWidth: 70 }}>LCL</th>
              <th style={{ minWidth: 100 }}>Type</th>
              <th style={{ minWidth: 120 }}>Tool</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length > 0 ? filtered.map((item, idx) => (
              <tr key={idx}>
                <td>{item._global_no}</td>
                <td style={{ fontSize: '0.74rem', color: '#6b7280', maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {item._source}
                </td>
                <td>{item.location_on_drawing || '—'}</td>
                <td>
                  <span className={`cc-badge ${item.critical_characteristic === 'Yes' ? 'yes' : 'no'}`}>
                    {item.critical_characteristic}
                  </span>
                </td>
                <td style={{ maxWidth: 280, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {item.specification || '—'}
                </td>
                <td>{fmtLimit(item.nominal)}</td>
                <td>{fmtTolPlus(item.tolerance_plus)}</td>
                <td>{fmtTolMinus(item.tolerance_minus)}</td>
                <td>{fmtLimit(item.ucl)}</td>
                <td>{fmtLimit(item.lcl)}</td>
                <td>{item.inspection_type || '—'}</td>
                <td>{item.inspection_tool || '—'}</td>
              </tr>
            )) : (
              <tr>
                <td colSpan="12" style={{ textAlign: 'center', color: '#94a3b8', padding: 20 }}>
                  No items match your search/filter.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <p style={{ marginTop: 10, fontSize: '0.78rem', color: '#9C9490' }}>
        Read-only combined view — open individual files below to edit inspection items.
      </p>
    </div>
  );
}

/* ── Batch results container ─────────────────────────────────── */
export default function BatchResultsView({
  queue,
  apiKey,
  approvalPending,
  approvalDone,
  onShareClick,
  onResultUpdated,
  pipelineTelemetry,
  logsPanelRef,
}) {
  const completed = queue.filter(item => item.status === 'completed');
  const failed    = queue.filter(item => item.status === 'failed');

  const allResultsLoaded =
    completed.length > 0 && completed.every((i) => i.results != null);

  const completedWithResults = completed.filter(i => i.results != null);
  const isBatch = completedWithResults.length >= 2;

  const totalScanMs = completedWithResults.reduce(
    (s, i) => s + (Number(i.results?.scan_duration_ms) || 0),
    0,
  );
  const totalScanLabel = totalScanMs > 0 ? formatScanMs(totalScanMs) : null;

  // 'combined' tab is only meaningful for multi-file batches
  const [viewMode, setViewMode] = useState('per-file');

  const [dlAllOpen,  setDlAllOpen]  = useState(false);
  const [dlAllBusy,  setDlAllBusy]  = useState(false);
  const [dlAllError, setDlAllError] = useState(null);

  const completedIds = completed.map(i => i.fileId);

  // Optional refresh when landing on results — never replace an existing snapshot
  // with a higher item_count (first completion wins).
  const refetchedOnceRef = useRef(new Set());
  useEffect(() => {
    completed.forEach((item) => {
      if (!item.fileId) return;
      if (item.results != null) return;
      if (refetchedOnceRef.current.has(item.fileId)) return;
      refetchedOnceRef.current.add(item.fileId);
      API.getResults(item.fileId)
        .then((fresh) => {
          if (!fresh) return;
          onResultUpdated(item.fileId, fresh);
        })
        .catch(() => { /* swallow — App.jsx prefetch handles failures */ });
    });
  // Re-run when the queue changes shape (e.g. a new file finishes).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completedIds.join(',')]);

  const handleDownloadAllExcel = async () => {
    setDlAllOpen(false);
    setDlAllBusy(true);
    setDlAllError(null);
    try { await API.downloadZipExcel(completedIds); }
    catch (err) { setDlAllError(err.error || 'ZIP download failed'); }
    finally { setDlAllBusy(false); }
  };

  const handleDownloadAllPdf = async () => {
    setDlAllOpen(false);
    setDlAllBusy(true);
    setDlAllError(null);
    try { await API.downloadZipPdf(completedIds); }
    catch (err) { setDlAllError(err.error || 'ZIP download failed'); }
    finally { setDlAllBusy(false); }
  };

  return (
    <div className="brs-root">

      {/* Summary header */}
      <div className="brs-summary-header">
        <div>
          <h2>
            {completed.length === queue.length
              ? `${completed.length} scan${completed.length !== 1 ? 's' : ''} complete`
              : `${completed.length} of ${queue.length} scans completed`}
          </h2>
          <p>
            {completed.length > 0 && `${completed.reduce((s, i) => s + (i.results?.item_count || 0), 0)} total inspection items`}
            {failed.length > 0 && ` · ${failed.length} failed`}
            {totalScanLabel && ` · Total scan time ${totalScanLabel}`}
          </p>
        </div>

        {/* Download All — only shown when 2+ files completed */}
        {completed.length > 1 && (
          <div style={{ position: 'relative' }}>
            {dlAllError && (
              <div className="brs-dl-error" style={{ marginBottom: 6 }}>
                <span>{dlAllError}</span>
                <button onClick={() => setDlAllError(null)}>×</button>
              </div>
            )}
            <button
              type="button"
              className="download-button primary"
              onClick={() => setDlAllOpen(o => !o)}
              disabled={dlAllBusy || !allResultsLoaded}
              title={!allResultsLoaded ? 'Waiting for all inspection payloads to load' : undefined}
            >
              {dlAllBusy ? 'Preparing ZIP…' : !allResultsLoaded ? 'Loading results…' : '↓ Download All ▾'}
            </button>
            {dlAllOpen && (
              <div className="brs-dl-menu">
                <button className="brs-dl-menu-item" onClick={handleDownloadAllExcel}>
                  All Excel files (.zip)
                </button>
                <button className="brs-dl-menu-item" onClick={handleDownloadAllPdf}>
                  All PDF files (.zip)
                </button>
              </div>
            )}
          </div>
        )}

        {/* Show approval banner / badge only for the last completed scan */}
        {approvalPending && (
          <div className="brs-approval-banner">
            <div className="brs-approval-icon">!</div>
            <div>
              <div className="brs-approval-title">Approval required</div>
              <div className="brs-approval-sub">Review the inspection plans below, then submit for review.</div>
            </div>
            <button className="brs-approval-btn" onClick={onShareClick}>
              Ask for Approval →
            </button>
          </div>
        )}
        {approvalDone && (
          <div className="brs-approval-done">
            <span className="brs-approval-check">✓</span>
            <span>Approval decision submitted successfully.</span>
          </div>
        )}
      </div>

      {/* View-mode tabs — only for multi-file batches with results loaded */}
      {isBatch && allResultsLoaded && (
        <div style={{ display: 'flex', gap: 0, margin: '8px 0 0', borderBottom: '2px solid #E8E2D9' }}>
          <button
            onClick={() => setViewMode('per-file')}
            style={{
              padding: '8px 20px',
              fontWeight: 600,
              fontSize: '0.85rem',
              border: 'none',
              borderBottom: viewMode === 'per-file' ? '2px solid #1F3864' : '2px solid transparent',
              background: 'none',
              color: viewMode === 'per-file' ? '#1F3864' : '#9C9490',
              cursor: 'pointer',
              marginBottom: -2,
            }}
          >
            Per File
          </button>
          <button
            onClick={() => setViewMode('combined')}
            style={{
              padding: '8px 20px',
              fontWeight: 600,
              fontSize: '0.85rem',
              border: 'none',
              borderBottom: viewMode === 'combined' ? '2px solid #059669' : '2px solid transparent',
              background: 'none',
              color: viewMode === 'combined' ? '#059669' : '#9C9490',
              cursor: 'pointer',
              marginBottom: -2,
            }}
          >
            Combined View ({completedWithResults.reduce((s, i) => s + (i.results?.item_count || 0), 0)} items)
          </button>
        </div>
      )}

      {/* Failed files notice */}
      {failed.length > 0 && (
        <div className="brs-failed-notice">
          <strong>{failed.length} file{failed.length !== 1 ? 's' : ''} could not be processed:</strong>
          <ul>
            {failed.map(item => (
              <li key={item.fileId}>
                {item.fileName} — {item.error || 'Unknown error'}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Combined view: all items from all files in one table */}
      {viewMode === 'combined' && isBatch && allResultsLoaded && (
        <div className="brs-section">
          <div style={{ padding: '16px 0 8px' }}>
            <h3 style={{ margin: 0, fontSize: '1rem', color: '#1A1410', fontWeight: 700 }}>
              Combined Inspection Plan
            </h3>
            <p style={{ margin: '4px 0 0', fontSize: '0.82rem', color: '#6b7280' }}>
              All {completedWithResults.length} files merged — matches the dashboard batch total
            </p>
          </div>
          <CombinedResultsView completedWithResults={completedWithResults} />
        </div>
      )}

      {/* Result sections — one per completed file.
          For multi-file batches, only visible in Per File mode.
          For single-file scans, always visible (viewMode stays 'per-file'). */}
      {viewMode === 'per-file' && completed.map((item, sectionIdx) =>
        item.results ? (
          <FileResultSection
            key={item.fileId}
            item={item}
            queueIdx={queue.indexOf(item)}
            totalFiles={completed.length}
            sectionIndex={sectionIdx}
            onResultUpdated={onResultUpdated}
          />
        ) : (
          <FileResultSectionPending
            key={item.fileId}
            item={item}
            totalFiles={completed.length}
            sectionIndex={sectionIdx}
          />
        )
      )}

      {completed.length === 0 && (
        <div className="brs-empty">
          No scans completed successfully. Please try uploading again.
        </div>
      )}
    </div>
  );
}
